import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { SITE, waLink, telLink } from "@/lib/site-config";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const header = document.querySelector("[data-testid='site-header']");
  const offset = header ? header.offsetHeight : 80;
  const y = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: Math.max(y, 0), behavior: "smooth" });
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { label: "Über mich", id: "about" },
    { label: "Leistungen", id: "services" },
    { label: "Resultate", id: "resultate" },
    { label: "FAQ", id: "faq" },
  ];

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass-strong border-b border-[rgba(255,255,255,0.14)]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-24 flex items-center justify-between gap-4">
        <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((it) => (
            <button
              key={it.id}
              onClick={() => scrollTo(it.id)}
              className="text-sm text-[#C9C6B4] hover:text-[#F3F1E8] transition-colors uppercase tracking-wider font-medium"
              data-testid={`nav-${it.id}`}
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={telLink()}
            className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full border border-[rgba(255,255,255,0.2)] text-[#F3F1E8] hover:bg-black/[0.04] transition-colors"
            aria-label="Anrufen"
            data-testid="header-call-btn"
          >
            <Phone size={16} />
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm rounded-full px-4 py-2.5 transition-colors"
            data-testid="header-whatsapp-btn"
          >
            <MessageCircle size={16} /> WhatsApp
          </a>
          <button
            onClick={() => scrollTo("lead-form")}
            className="hidden md:inline-flex items-center bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold text-sm uppercase tracking-wider px-5 py-2.5 rounded-full transition-colors"
            data-testid="header-cta-btn"
          >
            Beratung sichern
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-[rgba(255,255,255,0.2)] text-[#F3F1E8]"
            aria-label="Menü"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden glass-strong border-t border-[rgba(255,255,255,0.14)] overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navItems.map((it) => (
                <button
                  key={it.id}
                  onClick={() => {
                    setOpen(false);
                    // Wait for menu collapse animation (~250ms) before scrolling,
                    // otherwise layout shift disrupts smooth-scroll trajectory.
                    setTimeout(() => scrollTo(it.id), 320);
                  }}
                  className="text-left text-[#F3F1E8] text-lg font-medium hover:text-[#E8E5D0] transition-colors"
                  data-testid={`mobile-nav-${it.id}`}
                >
                  {it.label}
                </button>
              ))}
              <div className="flex gap-3 pt-3">
                <a
                  href={telLink()}
                  className="flex-1 flex items-center justify-center gap-2 border border-[rgba(255,255,255,0.2)] rounded-full py-3 text-sm font-bold"
                  data-testid="mobile-menu-call"
                >
                  <Phone size={16} /> Anrufen
                </a>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] rounded-full py-3 text-sm font-bold"
                  data-testid="mobile-menu-whatsapp"
                >
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
              <button
                onClick={() => {
                  setOpen(false);
                  scrollTo("lead-form");
                }}
                className="bg-[#E8E5D0] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-full py-3"
                data-testid="mobile-menu-cta"
              >
                Beratung sichern
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
